import { useMemo } from "react";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import { useLinks } from "hooks/useLinks";
import useScript from "hooks/useScript";
import { loadBrandParam, mapString } from "utils/common";
import { getEnvironment, loadAssetsCdnUrl } from "utils/env";
import { ExternalScripts } from "utils/externalScripts";
import { loadLocaleParam } from "utils/locale";
import { extractThemeFromBodyClasses, loadCustomThemes } from "utils/theme";
import Decoration from "./components/layout/Decoration";
import Layout from "./components/layout/Layout";
import Footer from "./components/shared/Footer";
import Header from "./components/shared/Header";
import Modal from "./components/shared/Modal";
import { Routes } from "./components/shared/Routes";
import appConfig from "./config.json";

const mainAppPaths = ["/help", "/faq"];

const {
	routes,
	environments,
	cdn,
	brands,
	navigation,
	defaultImageDecoration,
} = appConfig;
const mainClass = "help-wrapper";
const env = getEnvironment();
const config = environments[env];
const brand = loadBrandParam();
const brandConfig = brands?.[brand];
const cdnAssetsUrl = loadAssetsCdnUrl(`${cdn?.baseUrl}`, env);
const helpAssetsUrl = loadAssetsCdnUrl(
	`${cdn?.baseUrl}${cdn?.helpAssetsPath}`,
	env,
	true
);
const currentLocale = loadLocaleParam();
document.documentElement.lang = currentLocale;

const defaultImageUrl = defaultImageDecoration && `${helpAssetsUrl}/images/${defaultImageDecoration}`

const getNavigationParameters = (defaultQuery) => {
	const queryObject = { ...defaultQuery, ...brandConfig?.navigationParameters };
	let queryString = "";
	for (const key in queryObject) {
		queryString += `${key}=${queryObject[key]}&`;
	}
	return queryString.substring(0, queryString.length - 1);
};

const isMainApp = () => {
	const { pathname } = window.location;
	for (let index = 0; index < mainAppPaths.length; index++) {
		if (pathname.includes(mainAppPaths[index])) {
			return true;
		}
	}
	return false;
};

if (isMainApp()) {
	document.body.classList.add("help-and-faq-background");
}

const loadNavigation = () => {
	let headerUrl = "";
	let footerUrl = "";
	if (brandConfig?.syndicatedHeader && brandConfig?.syndicatedFooter) {
		const baseUrl = config.syndicatedBaseUrl || window.location.origin;
		headerUrl = `${baseUrl}${brandConfig.syndicatedHeader.url}&locale=${currentLocale}&language=${currentLocale}`;
		footerUrl = `${baseUrl}${brandConfig.syndicatedFooter.url}&locale=${currentLocale}&language=${currentLocale}`;
	} else {
		headerUrl = mapString(
			`${cdnAssetsUrl}${navigation.cdnUrl}?${getNavigationParameters(
				navigation.query
			)}&brand=${brand}`,
			env === "development" ? "latest" : env
		);
	}

	if (!isMainApp()) {
		headerUrl += "&additionalProfileInfo=true";
	}

	return { headerUrl, footerUrl };
};

const App = () => {
	let { headerUrl, footerUrl } = loadNavigation();
	if (
		brandConfig.userAgentToPreventLoadingNavigationApp &&
		window?.navigator?.userAgent
			?.toLowerCase()
			.includes(brandConfig.userAgentToPreventLoadingNavigationApp)
	) {
		headerUrl = "";
		footerUrl = "";
	}

	const linkList = useMemo(() => {
		const assets = {
			dnsPrefetch: [
				cdnAssetsUrl,
				"https://go4.disney.go.com",
				"https://fonts.twdc.com",
			],
			preconnect: [
				cdnAssetsUrl,
				"https://go4.disney.go.com",
				"https://fonts.twdc.com",
			],
			css: [
				"https://fonts.twdc.com/InspireTWDC/latest.css",
				"https://fonts.twdc.com/InspireTWDC/2024.1.0/font.css",
				`${cdnAssetsUrl}/media/layout/assets/css/pepFontIcon.css`,
			],
			fonts: [
				"InspireTWDC-Roman.woff2",
				"InspireTWDC-Heavy.woff2",
				"InspireTWDC-Italic.woff2",
				"InspireTWDC-Light.woff2",
				"InspireTWDC-Medium.woff2",
				"InspireTWDC-Black.woff2",
			].map(font => `https://fonts.twdc.com/InspireTWDC/2024.1.0/woff/${font}`),
			fontIcons: [`${cdnAssetsUrl}/media/layout/assets/fonts/icons/pep_icon_font-webfont.woff2?version=2024.7.1`],
			scripts: [
				"https://go4.disney.go.com/",
				headerUrl,
				footerUrl,
			],
			images: [defaultImageUrl],
		}

		return [
			...assets.dnsPrefetch.map(href => ({ rel: "dns-prefetch", href })),
			...assets.preconnect.map(href => ({ rel: "preconnect", href, crossOrigin: "anonymous" })),
			...assets.css.map(href => ({ rel: "preload", href, as: "style", crossOrigin: "anonymous" })),
			...assets.fonts.map(href => ({ rel: "preload", href, as: "font", type: "font/woff2", crossOrigin: "anonymous" })),
			...assets.fontIcons.map(href => ({ rel: "preload", href, as: "font", type: "font/woff2", crossOrigin: "anonymous" })),
			...assets.scripts.map(href => ({ rel: "preload", href, as: "script", crossOrigin: "anonymous" })),
			...assets.images.map(href => ({ rel: "preload", href, as: "image", fetchPriority: "high" })),
		];
	}, [headerUrl, footerUrl]);
	useLinks(linkList);
	useScript(headerUrl, "head");
	useScript(footerUrl, "head");
	extractThemeFromBodyClasses(helpAssetsUrl, brand);
	loadCustomThemes(helpAssetsUrl, brand);

	return (
		<div className={`${mainClass}__main`}>
			<Header />
			<ExternalScripts />
			<div className={`${mainClass}__glow-container`}>
				<div className={`${mainClass}__glow`}></div>
				<Decoration
					src={defaultImageUrl}
					brand={brand}
					mainClass={mainClass}
				/>
			</div>
			<Router>
				<div
					className={`${mainClass}__container ${mainClass}__container--${currentLocale}`}
				>
					<Switch>
						{routes.map((route, i) => (
							<Routes key={i} path={route.path} component={Layout} />
						))}
					</Switch>
					<Modal />
				</div>
			</Router>
			{!config?.brands?.[brand]?.hideChat &&
				!(brandConfig?.chatAllowedLocales?.indexOf(currentLocale) === -1) && (
					<div
						id="lpChatButton"
						className={`help-chat help-chat--${brand}`}
						tabIndex="0"
						role="button"
					></div>
				)}
			<Footer />
		</div>
	);
};

export default App;
